import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { QRCodeCanvas } from "qrcode.react";
import { url, API_KEY } from "../../Constant/TextConstant";
import { toast } from "react-toastify";
import { useAuth } from "../../utils/AuthContext";
import { useCart } from "../../utils/cartContext";
import "./CheckoutScreen.css";
import "react-toastify/dist/ReactToastify.css";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import Footer from "../Footer/Footer";
import Header from "../Header";
import { Helmet } from "react-helmet";
import {
  fetchUniqueIds,
  sendFCMNotification,
} from "../../Services/NotificationServices";
import { useUserLocation } from "../../utils/useUserLocation";
import VoucherInfoCard from "./VoucherInfoCard ";
import AddressDialog from "./AddressDialog";

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, currentUserName } = useAuth();
  const { cart: contextCart, clearCart } = useCart();
  // Get fromCart from location state, default to true if not provided
  const fromCart = location.state?.fromCart ?? true;

  // Initialize cart state with either location.state or contextCart
  const [cart, setCart] = useState(() => {
    return location.state?.cart || contextCart || [];
  });

  // Address state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [existingAddresses, setExistingAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedAddressData, setSelectedAddressData] = useState(null);

  //Store Details
  const [storeDetails, setStoreDetails] = useState(null);
  const [storeName, setStoreName] = useState("");

  // Form state
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [locality, setLocality] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [fullAddress, setFullAddress] = useState("");
  const [orderInstructions, setOrderInstructions] = useState("");

  // Payment state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [upiId, setUpiId] = useState("");
  const [selectedUPIReceipt, setSelectedUPIReceipt] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  // Coin and voucher state
  const [availableCoins, setAvailableCoins] = useState(0);
  const [realAmountToApply, setRealAmountToApply] = useState(0);
  const [isVoucherApplied, setIsVoucherApplied] = useState(false);
  const [isVoucherEligible, setIsVoucherEligible] = useState(false);

  const phoneNumber = currentUser;

  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  // Handle cases where cart comes from context
  useEffect(() => {
    if (
      contextCart.length > 0 &&
      (!location.state?.cart || location.state.cart.length === 0)
    ) {
      setCart(contextCart);
    }
  }, [contextCart, location.state]);

  useEffect(() => {
    initializeData();
  }, [phoneNumber, cart]);

  useEffect(() => {
    if (cart.length > 0) {
      const realAmount = calculateRealAmountToApply();
      setRealAmountToApply(realAmount);
      setIsVoucherEligible(realAmount > 0);
    }
  }, [availableCoins, cart]);

  const initializeData = async () => {
    if (!phoneNumber || cart.length === 0) {
      return;
    }
    await Promise.all([
      fetchAddresses(),
      fetchAvailableCoins(),
      fetchStoreDetails(),
    ]);
  };

  const calculateRealAmountToApply = () => {
    const cartTotal = calculateSubtotal();
    const coins = availableCoins;

    if (coins <= 0) {
      return 0;
    }

    if (cartTotal >= 0 && cartTotal < 300) {
      if (coins >= 50) {
        return Math.floor(Math.random() * 20) + 1;
      } else {
        return Math.floor(coins / 3); // Convert to integer
      }
    }

    if (cartTotal >= 300 && cartTotal < 500) {
      if (coins >= 60) {
        return Math.floor(Math.random() * 40) + 1;
      } else {
        return Math.floor(coins / 2); // Convert to integer
      }
    }

    if (cartTotal >= 500 && cartTotal < 800) {
      if (coins >= 100) {
        return Math.floor(Math.random() * 60) + 1;
      } else {
        return Math.floor(coins / 2); // Convert to integer
      }
    }

    if (cartTotal >= 800) {
      if (coins >= 150) {
        return Math.floor(Math.random() * 50) + 1;
      } else {
        return Math.floor(coins / 2); // Convert to integer
      }
    }

    return 0;
  };

  const handleUseVoucher = () => {
    setIsVoucherApplied(true);
  };

  const handleRemoveVoucher = () => {
    setIsVoucherApplied(false);
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(
        `${url.baseAPIUrl}/api/Checkout/BindUserTblAddress?CustomerMobile=${phoneNumber}`,
        { timeout: 15000 }
      );

      // Check if we have cart items with coordinates
      let originLat = 0.0;
      let originLng = 0.0;

      if (cart && cart.length > 0 && cart[0]) {
        const firstItem = cart[0];
        originLat =
          typeof firstItem.Latitude === "string"
            ? parseFloat(firstItem.Latitude) || 0.0
            : Number(firstItem.Latitude) || 0.0;

        originLng =
          typeof firstItem.Longitude === "string"
            ? parseFloat(firstItem.Longitude) || 0.0
            : Number(firstItem.Longitude) || 0.0;
      }

      // If no valid origin coordinates, mark all addresses as invalid
      // const hasValidOrigin = originLat !== 0.0 && originLng !== 0.0;

      // Process addresses with distance calculation (only if we have valid origin)
      const updatedAddresses = await Promise.all(
        response.data.map(async (address) => {
          const lat = address.Latitude?.toString() || "";
          const lng = address.Longitude?.toString() || "";

          // Parse destination coordinates safely
          const destLat = parseFloat(lat) || 0.0;
          const destLng = parseFloat(lng) || 0.0;

          let distance = 0;
          let isValid = false;


          distance = await getDistance(originLat, originLng, destLat, destLng);
          isValid = distance !== null && distance <= 10000; // 10km limit
         

          return {
            id: address.ID,
            name: address.Name,
            mobile: address.Mobile,
            locality: address.Locality,
            city: address.City,
            state: address.State,
            pinCode: address.PinCode,
            fullAddress: address.FullAddress,
            landmark: address.Landmark,
            latitude: lat,
            longitude: lng,
            distance: distance || 0,
            isValid: isValid,
          };
        })
      );

      setExistingAddresses(updatedAddresses);

      // Auto-select first valid address if none selected
      const firstValidAddress = updatedAddresses.find((addr) => addr.isValid);
      if (firstValidAddress && !selectedAddress) {
        handleAddressSelect(firstValidAddress);
      }
    } catch (error) {
      if (error.code === "ECONNABORTED") {
        toast.error("Address loading timed out. Please try again.");
      } else {
        toast.error("Failed to load addresses");
      }
    }
  };

  // Distance calculation function
  const getDistance = async (originLat, originLng, destLat, destLng) => {
    try {
      // Replace with your actual API key

      const url = `https://api.olamaps.io/routing/v1/distanceMatrix?origins=${originLat},${originLng}&destinations=${destLat},${destLng}&mode=driving&api_key=${API_KEY.OLA_API_KEY}`;

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.status === 200) {
        const data = response.data;
        if (
          data.rows &&
          data.rows[0] &&
          data.rows[0].elements &&
          data.rows[0].elements[0]
        ) {
          return data.rows[0].elements[0].distance; // in meters
        }
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  const handleAddAddress = () => {
    setCurrentAddress(null);
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEditAddress = (address) => {
    setCurrentAddress({
      id: address.id || address.ID,
      name: address.name || address.Name,
      mobile: address.mobile || address.Mobile,
      locality: address.locality || address.Locality,
      city: address.city || address.City,
      state: address.state || address.State,
      pinCode: address.pinCode || address.PinCode || address.Pincode,
      fullAddress: address.fullAddress || address.FullAddress,
      landmark: address.landmark || address.Landmark,
      latitude: address.latitude || address.Latitude || 0,
      longitude: address.longitude || address.Longitude || 0,
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Are you sure you want to delete this address?")) {
      return;
    }

    try {
      const response = await axios.post(
        `${url.baseAPIUrl}/api/Checkout/DeleteAddress`,
        Number(addressId),
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Address deleted successfully");
        fetchAddresses();
      } else {
        throw new Error("Failed to delete address");
      }
    } catch (error) {
      toast.error("Failed to delete address");
    }
  };

  const saveAddress = async (addressData) => {
    try {
      const data = {
        Name: addressData.name,
        RegisteredMobile: phoneNumber,
        Locality: addressData.locality,
        City: addressData.city,
        Mobile: addressData.mobile,
        State: addressData.state,
        PinCode: addressData.pinCode,
        FullAddress: addressData.fullAddress,
        Landmark: addressData.landmark || "",
        Latitude: addressData.latitude || 0,
        Longitude: addressData.longitude || 0,
        ...(isEditing && currentAddress ? { ID: currentAddress.id } : {}),
      };

      const endpoint = isEditing
        ? `${url.baseAPIUrl}/api/Checkout/UpdateAddress`
        : `${url.baseAPIUrl}/api/Checkout/SaveAddress`;

      const response = await axios.post(endpoint, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.status === 200) {
        toast.success(
          `Address ${isEditing ? "updated" : "saved"} successfully`
        );
        fetchAddresses();
        setIsDialogOpen(false);
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }
    } catch (error) {
      toast.error(`Failed to ${isEditing ? "update" : "save"} address`);
    }
  };

  const fetchStoreDetails = async () => {
    try {
      if (cart.length === 0) return;

      const response = await axios.get(
        `${url.baseAPIUrl}/api/ShopKeeper/binddetails?StoreMobile=${cart[0].StoreMobile}`
      );
      setStoreDetails(response.data);
      setStoreName(response.data.StoreName);
      setUpiId(response.data.UpiId || "");
    } catch (error) {
    }
  };

  const fetchAvailableCoins = async () => {
    try {
      if (cart.length === 0) return;

      const response = await axios.get(
        `${url.baseAPIUrl}/api/reward/getTotalCashbackForCustomer?customerMobile=${phoneNumber}&StoreMobile=${cart[0].StoreMobile}`
      );
      setAvailableCoins(response.data || 0);
    } catch (error) {
    }
  };

  // Calculate order totals
  const cartTotal = () => {
    if (location.state?.cartTotal) {
      return location.state.cartTotal;
    }
    return cart.reduce(
      (total, item) => total + (item.OurPrice * item.quantity || 0),
      0
    );
  };

  const calculateSubtotal = () => cartTotal();

  const calculateDeliveryCharge = () => {
    const subtotalAfterDiscount =
      calculateSubtotal() - (isVoucherApplied ? realAmountToApply : 0);
    return subtotalAfterDiscount >= 299 ? 0 : 25;
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = isVoucherApplied ? realAmountToApply : 0;
    const deliveryCharge = calculateDeliveryCharge();
    return subtotal - discount + deliveryCharge;
  };

  const handleAddressSelect = (address) => {
    if (!address.isValid) {
      toast.error(
        "This address is outside our delivery area. Please select another address."
      );
      return;
    }

    setSelectedAddress(address.id);
    setSelectedAddressData(address);
    setName(address.name);
    setMobile(address.mobile);
    setLocality(address.locality);
    setCity(address.city);
    setState(address.state);
    setPinCode(address.pinCode);
    setFullAddress(address.fullAddress);

    // toast.success("Address selected successfully");
  };

  const placeOrder = async () => {
    if (!selectedAddress) {
      toast.error("Please select an address");
      return;
    }
    // Validate address validity
    const selectedAddr = existingAddresses.find(
      (addr) => addr.id === selectedAddress
    );
    if (!selectedAddr || !selectedAddr.isValid) {
      toast.error(
        "Selected address is outside our delivery area. Please choose a different address."
      );
      return;
    }
    if (!selectedPaymentMethod) {
      toast.error("Please select a payment method");
      return;
    }
    if (cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    setIsPlacingOrder(true);

    try {
      const timestamp = Date.now();
      const invoiceNumber = `NearMe-IND-INV-01${timestamp}`;
      const orderNumber = `NearMe-IND-ORD-01${timestamp}`;
      const shipmentNumber = `NearMe-IND-SHP-01${timestamp}`;

      const now = new Date();
      const currentTime = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const currentDate = now.toISOString().split("T")[0];

      const grandTotal = calculateGrandTotal();
      const deliveryCharge = calculateDeliveryCharge();
      const subtotal = calculateSubtotal();
      const discountApplied = isVoucherApplied ? realAmountToApply : 0;

      const orderData = {
        CustomerMobile: phoneNumber,
        InvoiceNumber: invoiceNumber,
        OrderNumber: orderNumber,
        ShipmentNumber: shipmentNumber,
        Date: currentDate,
        Name: name || selectedAddressData?.name || "N/A",
        Email: "",
        Locality: locality || selectedAddressData?.locality || "N/A",
        City: city || selectedAddressData?.city || "N/A",
        State: state || selectedAddressData?.state || "N/A",
        Pincode: pinCode || selectedAddressData?.pinCode || "000000",
        FullAddress: fullAddress || selectedAddressData?.fullAddress || "N/A",
        Total: grandTotal,
        Shipping: deliveryCharge,
        Time: currentTime,
        Payment_Status: getPaymentStatus(),
        StoreMobile: cart[0]?.StoreMobile || "N/A",
        customercashback: discountApplied.toString(),
        DeliveryPin: generatePinCode(),
        AlternateMobile: mobile || selectedAddressData?.mobile || "N/A",
        PaymentImage: selectedUPIReceipt ? "Will be uploaded" : "None",
        OrderInstruction: orderInstructions || "",
      };

      const orderPromises = cart.map(async (item) => {
        const productOrderData = {
          ...orderData,
          ProductID:
            item.ID || item.id || item.ProductId || item.ProductID || 0,
          Category: item.Category || "N/A",
          SubCategory1: item.SubCategory1 || "N/A",
          Subcategory2: item.SubCategory2 || "N/A",
          ProductName: item.ProductName || "Unknown",
          BrandName: item.BrandName || "",
          ProductCode: item.ProductCode || "N/A",
          Size: item.Size || "N/A",
          ColorName: item.ColorName || "N/A",
          Price: item.OurPrice || item.Price || 0,
          Qty: item.quantity || item.Quantity || 1,
          Image: item.Image1 ? item.Image1.split("/").pop() : "",
          cashback: discountApplied,
        };

        if (selectedUPIReceipt && cart.indexOf(item) === 0) {
          const paymentImageUrl = await uploadImage(selectedUPIReceipt);
          productOrderData.PaymentImage = paymentImageUrl;
        }

        return productOrderData;
      });

      const ordersToSubmit = await Promise.all(orderPromises);
      const submissionPromises = ordersToSubmit.map((order) => {
        return axios.post(`${url.baseAPIUrl}/api/Checkout/InsertOrder`, order, {
          headers: {
            "Content-Type": "application/json",
          },
        });
      });

      await Promise.all(submissionPromises);

      // Subtract coins only if voucher was applied
      if (isVoucherApplied && discountApplied > 0) {
        await subtractCoins(discountApplied);
      }

      const navigationState = {
        products: cart.map((item) => ({
          ID: item.id || item.ProductId || item.ProductID,
          ProductName: item.ProductName,
          OurPrice: item.OurPrice || item.Price,
          Mrp: item.Mrp,
          Quantity: item.quantity || item.Quantity || 1,
          Total:
            item.OurPrice * item.quantity || item.Price * item.Quantity || 0,
          Image1: item.Image1,
          StoreMobile: item.StoreMobile,
          Category: item.Category,
          SubCategory1: item.SubCategory1,
          SubCategory2: item.SubCategory2,
          Cashback: discountApplied,
          Size: item.Size,
          ColorName: item.ColorName,
        })),
        username: cart[0]?.StoreMobile || "",
        orderNumber: orderNumber,
        cartTotal: grandTotal,
        discount: discountApplied,
        subTotal: grandTotal,
      };

      if (fromCart) {
        clearCart();
      }
     

      // Send notifications
      const uniqueIds = await fetchUniqueIds(cart[0].StoreMobile);
      if (uniqueIds) {
        await sendFCMNotification({
          Tokens: uniqueIds,
          Title: "New Order Placed",
          Body: `A new order has been placed by ${
            currentUserName || "Customer"
          }`,
          ScreenRoute: "/orderDetails",
        });
      }

      const supportUniqueIds = await fetchUniqueIds("9523812734");
      if (supportUniqueIds) {
        await sendFCMNotification({
          Tokens: supportUniqueIds,
          Title: "New Order Placed",
          Body: `A new order has been placed by ${
            currentUserName || "Customer"
          } to ${cart[0].StoreName}`,
          ScreenRoute: "/supportOrderDetails",
        });
      }
       navigate("/successScreen", { state: navigationState });

      toast.success("Order placed successfully");
    } catch (error) {
      toast.error(
        "An error occurred while placing your order ",
        error.toString
      );
    } finally {
      setIsPlacingOrder(false);
    }
  };

  const getPaymentStatus = () => {
    switch (selectedPaymentMethod) {
      case 1:
        return "Cash On Delivery";
      case 2:
        return "Online Payment";
      case 3:
        return "Pick from Store";
      default:
        return "Pending";
    }
  };

  const generatePinCode = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const subtractCoins = async (amountToSubtract) => {
    try {
      await axios.post(
        `${url.baseAPIUrl}/api/reward/UpdateCustomerCashbackSubtract`,
        {
          CustomerMobile: phoneNumber,
          StoreMobile: cart[0].StoreMobile,
          UpdatedCashback: amountToSubtract,
        }
      );
      // Update local state
      setAvailableCoins((prev) => prev - amountToSubtract);
    } catch (error) {
      toast.error("Failed to update coins");
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${url.baseAPIUrl}/api/UploadImage/UploadProductImage`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data.replace(/"/g, "").trim();
    } catch (error) {
      return null;
    }
  };

  const handleFileChange = (e) => {
    setSelectedUPIReceipt(e.target.files[0]);
  };

  const renderQRCode = () => {
    if (cart.length === 0 || !upiId) return <div>Invalid UPI details</div>;

    const storeName = encodeURIComponent(cart[0].StoreName || "Merchant");
    const transactionId = `ORD${Date.now()}${
      Math.floor(Math.random() * 9000) + 1000
    }`;
    const grandTotal = calculateGrandTotal();
    const qrData = `upi://pay?pa=${upiId}&pn=${storeName}&mc=0000&tid=${transactionId}&vp=1&mn=${storeName}&am=${grandTotal}&cu=INR&url=https://yourstore.com`;

    return (
      <div className="checkout-qr-container">
        <QRCodeCanvas
          value={qrData}
          size={350}
          level="H"
          includeMargin={true}
        />
        <p className="checkout-qr-amount">Amount: ₹{grandTotal.toFixed(2)}</p>
      </div>
    );
  };

  const renderAddressSection = () => (
    <section className="checkout-section">
      <div className="checkout-section-header">
        <h2>Delivery Address</h2>
        <button className="checkout-add-address-btn" onClick={handleAddAddress}>
          <FaPlus /> Add New Address
        </button>
      </div>

      <div className="checkout-address-grid">
        {existingAddresses.map((address) => (
          <div
            key={address.id}
            className={`checkout-address-card ${
              selectedAddress === address.id ? "checkout-address-selected" : ""
            } ${!address.isValid ? "checkout-address-invalid" : ""}`}
          >
            <div className="checkout-address-content">
              <input
                type="radio"
                id={`checkout-address-${address.id}`}
                name="checkout-address"
                checked={selectedAddress === address.id}
                onChange={() => handleAddressSelect(address)}
                disabled={!address.isValid}
              />
              <label htmlFor={`checkout-address-${address.id}`}>
                <h3>{address.name}</h3>
                <p className="checkout-address-mobile">{address.mobile}</p>
                <p className="checkout-address-full">{address.fullAddress}</p>
                <p className="checkout-address-locality">
                  {address.locality}, {address.city}, {address.state} -{" "}
                  {address.pinCode}
                </p>
                {address.distance > 0 && (
                  <p className="checkout-address-distance">
                    Distance: {(address.distance / 1000).toFixed(1)} km
                  </p>
                )}
                {!address.isValid && (
                  <p className="checkout-address-invalid-label">
                    ⚠️ Outside delivery area
                  </p>
                )}
              </label>
            </div>
            <div className="checkout-address-actions">
              <button
                className="checkout-edit-btn"
                onClick={() => handleEditAddress(address)}
              >
                <FaEdit />
              </button>
              <button
                className="checkout-delete-btn"
                onClick={() => handleDeleteAddress(address.id)}
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
      <AddressDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={saveAddress}
        initialData={currentAddress || {}}
        isEditing={isEditing}
      />
    </section>
  );

  const generateStructuredData = () => {
    if (cart.length === 0) return null;

    const orderItems = cart.map((item) => ({
      "@type": "Product",
      name: item.ProductName,
      sku: item.ProductCode,
      image: item.Image1
        ? `${url.productImageStartingPath}/${item.Image1}`
        : "",
      price: item.OurPrice,
      priceCurrency: "INR",
      Quantity: item.Quantity,
    }));

    return {
      "@context": "https://schema.org",
      "@type": "CheckoutPage",
      name: "Checkout - Complete Your Purchase",
      description: "Review your order and complete your purchase securely",
      url: window.location.href,
      potentialAction: {
        "@type": "OrderAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${window.location.origin}/checkout`,
          actionPlatform: [
            "http://schema.org/DesktopWebPlatform",
            "http://schema.org/MobileWebPlatform",
          ],
        },
        deliveryMethod: "http://schema.org/ParcelDelivery",
      },
      itemListElement: orderItems,
      totalPrice: calculateGrandTotal(),
      priceCurrency: "INR",
      seller: {
        "@type": "Organization",
        name: cart[0]?.StoreName || "MyStore",
      },
    };
  };

  const structuredData = generateStructuredData();

  if (cart.length === 0) {
    return (
      <>
        <Helmet>
          <title>Empty Cart | MyStore</title>
          <meta name="description" content="Your shopping cart is empty" />
          <meta name="keywords" content="empty cart, shopping, ecommerce" />
        </Helmet>
        <div className="checkout-empty-cart">
          <h1>Your Shopping Cart is Empty</h1>
          <button
            className="checkout-continue-shopping-btn"
            onClick={() => navigate("/")}
          >
            Continue Shopping
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Checkout - Complete Your Purchase | MyStore</title>
        <meta
          name="description"
          content={`Complete your purchase of ${cart.length} items with secure payment options`}
        />
        <meta
          name="keywords"
          content="checkout, secure payment, online shopping, ecommerce, purchase, delivery"
        />
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      <Header
        userLocation={currentLocation}
        setShowModal={setShowModal}
        setUserLocation={updateLocation}
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="checkout-page-container">
        <header className="checkout-page-header">
          <button className="checkout-back-button" onClick={() => navigate(-1)}>
            &larr; Back
          </button>
          <h1>Checkout</h1>
        </header>

        <main className="checkout-page-main">
          <div className="checkout-left-column">
            {renderAddressSection()}

            <section className="checkout-section">
              <h2>Order Summary</h2>
              <div className="checkout-order-items">
                {cart.map(
                  (item) => (
                    (
                      <div
                        key={`${item.ID}-${item.Size}-${item.ColorName}`}
                        className="checkout-order-item"
                      >
                        <img
                          src={
                            item.Image1
                              ? `${url.productImageStartingPath}/${item.Image1}`
                              : "/placeholder-product.jpg"
                          }
                          alt={item.ProductName}
                          className="checkout-item-image"
                        />
                        <div className="checkout-item-details">
                          <h3>{item.ProductName}</h3>
                          {item.Size && <p>Size: {item.Size}</p>}
                          {item.ColorName && <p>Color: {item.ColorName}</p>}
                          <p>Qty: {item.Quantity}</p>
                        </div>
                        <div className="checkout-item-price">
                          ₹
                          {(
                            (item?.OurPrice ?? item?.Price ?? 0) *
                            (item?.Quantity ?? 0)
                          ).toFixed(2)}
                        </div>
                      </div>
                    )
                  )
                )}
              </div>
            </section>

            <section className="checkout-section">
              <h2>Payment Method</h2>
              <div className="checkout-payment-methods">
                {/* Cash on Delivery */}
                <div className="checkout-payment-option">
                  <input
                    type="radio"
                    id="checkout-cod"
                    name="checkout-payment"
                    checked={selectedPaymentMethod === 1}
                    onChange={() => {
                      setSelectedPaymentMethod(1);
                      setShowPaymentOptions(false);
                    }}
                  />
                  <label htmlFor="checkout-cod">Cash on Delivery</label>
                </div>

                {/* Online Payment */}
                <div className="checkout-payment-option">
                  <input
                    type="radio"
                    id="checkout-online"
                    name="checkout-payment"
                    checked={selectedPaymentMethod === 2}
                    onChange={() => {
                      setSelectedPaymentMethod(2);
                      setShowPaymentOptions(true);
                    }}
                  />
                  <label htmlFor="checkout-online">Online Payment</label>
                </div>

                {/* Pick from Store */}
                <div className="checkout-payment-option">
                  <input
                    type="radio"
                    id="checkout-pickup"
                    name="checkout-payment"
                    checked={selectedPaymentMethod === 3}
                    onChange={() => {
                      setSelectedPaymentMethod(3);
                      setShowPaymentOptions(false);
                    }}
                  />
                  <label htmlFor="checkout-pickup">Pick from Store</label>
                </div>

                {showPaymentOptions && selectedPaymentMethod === 2 && (
                  <div className="checkout-online-payment-options">
                    <div className="checkout-payment-method">
                      <h3>Scan QR Code</h3>
                      {renderQRCode()}
                    </div>

                    <div className="checkout-payment-method">
                      <h3>Pay via UPI</h3>
                      <div className="checkout-upi-payment">
                        <div className="checkout-upi-id">
                          <span>{upiId}</span>
                          <button
                            className="checkout-copy-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(upiId);
                              toast.success("UPI ID copied to clipboard");
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="checkout-payment-method">
                      <h3>Upload Payment Receipt</h3>
                      <label className="checkout-receipt-upload">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                        {selectedUPIReceipt ? (
                          <div className="checkout-receipt-preview">
                            <img
                              src={URL.createObjectURL(selectedUPIReceipt)}
                              alt="Receipt"
                            />
                            <span>Change Receipt</span>
                          </div>
                        ) : (
                          <div className="checkout-upload-placeholder">
                            <span>+ Upload Payment Proof</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                )}

                {selectedPaymentMethod === 3 && storeDetails && (
                  <div className="checkout-pickup-info">
                    <h3>Store Pickup Information</h3>
                    <div className="checkout-pickup-details">
                      <p>
                        <strong>Store Name:</strong> {storeName}
                      </p>
                      <p>
                        <strong>Store Address:</strong>
                      </p>
                      <p>
                        {storeDetails.Address || "Store address not available"}
                      </p>

                      {storeDetails.StoreTiming && (
                        <p>
                          <strong>Store Hours:</strong>{" "}
                          {storeDetails.StoreTiming}
                        </p>
                      )}

                      <p>
                        <strong>Contact:</strong>{" "}
                        {cart[0]?.StoreMobile ||
                          storeDetails.StoreMobile ||
                          "Store contact not available"}
                      </p>

                      {storeDetails.StoreLandline && (
                        <p>
                          <strong>Landline:</strong>{" "}
                          {storeDetails.StoreLandline}
                        </p>
                      )}

                      <div className="checkout-pickup-instructions">
                        <h4>Pickup Instructions:</h4>
                        <ul>
                          <li>Please bring your order confirmation number</li>
                          {/* <li>
                            Valid government-issued ID proof may be required
                          </li> */}
                          <li>
                            Payment will be collected at the store counter
                          </li>
                          <li>
                            Items will be ready for pickup within 2 hours of
                            order confirmation
                          </li>
                          <li>Please check store timings before visiting</li>
                        </ul>
                      </div>

                      {storeDetails.Address && (
                        <div className="checkout-store-location">
                          <h4>Store Location:</h4>
                          <p>{storeDetails.Address}</p>
                          {storeDetails.Latitude && storeDetails.Longitude && (
                            <button
                              className="checkout-direction-btn"
                              onClick={() => {
                                // Open maps with store location
                                const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${storeDetails.Latitude},${storeDetails.Longitude}`;
                                window.open(mapsUrl, "_blank");
                              }}
                            >
                              📍 Get Directions
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedPaymentMethod === 3 && !storeDetails && (
                  <div className="checkout-pickup-info">
                    <p>Loading store information...</p>
                  </div>
                )}
              </div>
            </section>

            <section className="checkout-section">
              <h2>Delivery Instructions</h2>
              <textarea
                className="checkout-delivery-instructions"
                placeholder="Any special delivery instructions?"
                value={orderInstructions}
                onChange={(e) => setOrderInstructions(e.target.value)}
                maxLength="200"
              />
            </section>
          </div>

          <div className="checkout-right-column">
            <section className="checkout-payment-summary">
              <h2>Payment Summary</h2>
              <div className="checkout-summary-details">
                <div className="checkout-summary-row">
                  <span>
                    Subtotal (
                    {cart.reduce((sum, item) => sum + item.Quantity, 0)} items)
                  </span>
                  <span>₹{calculateSubtotal().toFixed(2)}</span>
                </div>

                <VoucherInfoCard
                  voucherAmount={availableCoins}
                  realAmountToApply={realAmountToApply}
                  isEligible={isVoucherEligible}
                  voucherType="Nearme"
                  onUseVoucher={
                    isVoucherApplied ? handleRemoveVoucher : handleUseVoucher
                  }
                  isApplied={isVoucherApplied}
                />

                {isVoucherApplied && (
                  <div className="checkout-summary-row checkout-discount-row">
                    <span>Coin Discount Applied</span>
                    <span>-₹{realAmountToApply.toFixed(2)}</span>
                  </div>
                )}

                <div className="checkout-summary-row">
                  <span>Delivery Charge</span>
                  <span>₹{calculateDeliveryCharge().toFixed(2)}</span>
                </div>

                <div className="checkout-summary-row checkout-total-row">
                  <span>Total Amount</span>
                  <span>₹{calculateGrandTotal().toFixed(2)}</span>
                </div>

                {/* <div className="checkout-summary-row checkout-available-coins">
                  <span>Available Coins</span>
                  <span>{availableCoins.toFixed(2)}</span>
                </div> */}
              </div>

              <button
                className="checkout-place-order-btn"
                onClick={placeOrder}
                disabled={
                  isPlacingOrder || !selectedAddress || !selectedPaymentMethod
                }
              >
                {isPlacingOrder ? (
                  <span className="checkout-spinner"></span>
                ) : (
                  `Pay ₹${calculateGrandTotal().toFixed(2)}`
                )}
              </button>

              {isVoucherApplied && (
                <button
                  className="checkout-remove-voucher-btn"
                  onClick={handleRemoveVoucher}
                >
                  Remove Coin Discount
                </button>
              )}
            </section>
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default Checkout;
